<template>
  <section class="deals-section">
    <div class="container">
      <h2 class="deals-title">Deals of the week</h2>

      <div class="deals-divider">
        <div class="line"></div>
        <div class="pagination-dots">
          <span
            class="dot"
            v-for="index in Math.ceil(dealsList.length / 2)"
            :key="index - 1"
            :class="{ active: index - 1 === 0 }"
            data-bs-target="#dealsCarousel"
            :data-bs-slide-to="index - 1"
          ></span>
        </div>
      </div>

      <div
        v-if="!dealsList.length"
        class="text-center text-muted py-5"
      >
        <i class="bi bi-box-seam d-block mb-2" style="font-size: 2rem"></i>
        <p class="mb-0">No products found.</p>
      </div>

      <div v-else class="deals-slider">
        <!-- Bootstrap Carousel -->
        <div
          id="dealsCarousel"
          class="carousel slide"
          data-bs-ride="carousel"
          data-bs-interval="2000"
        >
          <div class="carousel-inner">
            <div
              class="carousel-item"
              v-for="(slideDeals, index) in groupedDeals"
              :key="index"
              :class="{ active: index === 0 }"
            >
              <div class="row">
                <div class="col-md-6" v-for="deal in slideDeals" :key="deal.id">
                  <div class="deal-card">
                    <router-link
                      :to="'/product/' + deal.product._id"
                      style="text-decoration: none"
                    >
                      <img
                        :src="
                          deal.product?.images[0] ?
                          `${BASE_IMAGE_PREFIX}/${deal.product.images[0]}` :
                          `${PLACEHOLDER_IMAGE}`
                        "
                        :alt="deal.product.title"
                        class="deal-image"
                      />
                    </router-link>
                    <div class="deal-content">
                      <router-link
                        :to="'/product/' + deal.product._id"
                        style="text-decoration: none"
                      >
                        <h3 class="deal-title">{{ deal.product.title }}</h3>
                      </router-link>
                      <router-link
                        v-if="deal.product.productCategory !== 'stationary'"
                        :to="
                          '/brand/' + deal.product.brand.replace(/ /g, '_')
                        "
                        style="text-decoration: none"
                      >
                        <div class="deal-author">{{ deal.product.brand }}</div>
                      </router-link>
                      <div class="deal-price" v-if="deal.product.minVariant">
                        <span v-if="deal.product.minVariant.isOnSale && deal.product.minVariant.salePrice">
                          <span class="text-decoration-line-through text-muted me-1">
                            ৳ {{ deal.product.minVariant.price }}
                          </span>
                          <span>৳ {{ deal.product.minVariant.salePrice }}</span>
                        </span>
                        <span v-else>৳ {{ deal.product.minVariant.price }}</span>
                      </div>
                      <div
                        class="card-footer p-0 pb-4 pt-2 border-top-0 bg-transparent"
                      >
                        <div class="text-left d-flex gap-2 flex-wrap">
                          <span
                            class="btn btn-outline-dark d-inline-flex align-items-center text-uppercase"
                            @click="addToCart(deal.product)"
                            style="font-size: 10px"
                          >
                            <i class="bi bi-cart3 me-1"></i> Add to cart
                          </span>
                          <span
                            class="btn btn-outline-dark d-inline-flex align-items-center justify-content-center text-uppercase"
                            @click="addToWishlist(deal.product)"
                            style="font-size: 10px"
                          >
                            <i class="bi bi-suit-heart"></i>
                          </span>
                          <span
                            class="btn btn-outline-dark d-inline-flex align-items-center justify-content-center text-uppercase"
                            @click="addToCompare(deal.product)"
                            style="font-size: 10px"
                          >
                            <i class="bi bi-arrow-left-right"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { BASE_IMAGE_PREFIX, PLACEHOLDER_IMAGE } from '@/globals';
import { analytics } from '@/services/analytics';

export default {
  name: "DealsSlider",
  props: {
    dealsList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      BASE_IMAGE_PREFIX,
      PLACEHOLDER_IMAGE,
    }
  },
  computed: {
    groupedDeals() {
      const isMobileOrTablet = window.innerWidth < 768;
      const itemsPerGroup = isMobileOrTablet ? 1 : 2;

      const grouped = [];

      for (let i = 0; i < this.dealsList.length; i += itemsPerGroup) {
        grouped.push(this.dealsList.slice(i, i + itemsPerGroup));
      }

      return grouped;
    },
  },
  methods: {
    addToCart(product) {
      if (!product.minVariant) return;

      const cartItem = {
        product: product,
        quantity: 1,
        variant: product.minVariant
      };

      this.$store.dispatch("addToCart", cartItem);

      this.$root.showToast({
        message: "Item added to cart!",
        status: "success",
        duration: 3000,
      });

      analytics.addToCart(product,
        product.minVariant.salePrice || product.minVariant.price, 1)
    },
    addToWishlist(product) {
      const wishedItem = {
        ...product,
        quantity: 1,
        variant: product.minVariant
      };
      this.$store.dispatch("addToWishlist", wishedItem);
      this.$root.showToast({
        message: "Item added to wishlist!",
        status: "success",
        duration: 3000,
      });
      analytics.addToWishlist(product,
        product.minVariant.salePrice || product.minVariant.price, 1)
    },
    addToCompare(product) {
      const compareItems = this.$store.getters.compareItems;
      if (compareItems.includes(product._id)) {
        this.$root.showToast({
          message: "Product is already in compare list",
          status: "error",
          duration: 3000,
        });
        return;
      }
      if (compareItems.length >= 2) {
        this.$root.showToast({
          message: "Maximum 2 items can be compared at a time",
          status: "error",
          duration: 3000,
        });
        return;
      }
      this.$store.dispatch("addToCompare", product._id);
      this.$root.showToast({
        message: "Item added to compare",
        status: "success",
        duration: 3000,
      });
    },
  },
};
</script>

<style lang="scss">
.deals-title {
  font-size: 30px;
  font-weight: 600;
  margin-bottom: 10px;
}

.deals-divider {
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  .line {
    flex-grow: 1;
    height: 1px;
    background-color: #e7e7e7;
  }

  .pagination-dots {
    display: flex;
    margin-left: auto;

    .dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: #e7e7e7;
      margin: 0 5px;
      cursor: pointer;
      transition: background-color 0.3s ease;

      &.active {
        background-color: #f75454;
      }
    }
  }
}

.deals-slider {
  margin-bottom: 20px;
  position: relative;
  min-height: 320px;

  .carousel {
    .carousel-inner {
      position: relative;
      min-height: 320px;
    }
  }
}

.deal-card {
  display: flex;
  margin-bottom: 20px;

  .deal-image {
    width: 200px;
    flex-shrink: 0;
    border-radius: 8px;
    overflow: hidden;

    img {
      width: 100%;
      height: auto;
      object-fit: cover;
      transition: transform 0.3s ease;

      &:hover {
        transform: scale(1.05);
      }
    }
  }

  .deal-content {
    padding: 20px 30px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .deal-title {
    color: #333;
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 15px;
    transition: color 0.3s ease;

    &:hover {
      color: #f75454;
    }
  }

  .deal-author {
    font-size: 14px;
    color: #777;
    margin-bottom: 15px;
  }

  .deal-price {
    font-size: 28px;
    font-weight: bold;
    color: #f75454;
    margin-bottom: 15px;
  }
}

@media (max-width: 991px) {
  .deal-card {
    flex-direction: column;

    .deal-image {
      // width: 100%;

      img {
        height: 100px;
      }
    }

    .deal-content {
      padding: 20px 0;
    }
  }
}
</style>
